import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Circle, ArrowRight } from 'lucide-react';
import type { AttentionItem } from '../utils/programmeModel';
import ProvenanceBadge from './ProvenanceBadge';
import '../styles/AttentionList.css';

// AttentionList — the attention list of the value-proven board
// (029-value-proven-dashboard, Increment E; User Story 4, FR-010/FR-015/FR-016,
// SC-001/SC-008). A PURE view over the model's `attention` array: it renders
// EXACTLY the AttentionItem objects `deriveValueProven(...).attention` hands it,
// ONE <li> per input item, in order. It does NOT fetch, derive, or read the
// snapshot — it binds what it is GIVEN (the derive fn owns ranking, the per-item
// provenance, the single `urgent` flag and each confirmRoute).
//
// Honesty contract:
//   • REAL vs ILLUSTRATIVE legible at a glance (SC-001): an illustrative item
//     wears the shared ProvenanceBadge ("Illustrative" + accessible name
//     "Illustrative sample data"); a REAL item carries NO badge.
//   • RESERVED CORAL (SC-008 — the ONE place coral is allowed in the whole
//     dashboard): the SINGLE most-urgent item (`urgent: true`) opts in with
//     data-reserved-colour="urgent" (mirroring Dashboard.tsx's urgent NextItem)
//     and may use --ap-accent. A non-urgent item carries NO data-reserved-colour
//     and no coral; zero urgent items → zero coral.
//   • Urgent state is conveyed as TEXT (an "Urgent" label), never colour alone.
//   • Empty state: zero items render an honest message naming the absence, NOT a
//     fabricated item and no faked "0".
//   • a11y (FR-015/FR-016): a real <ul role="list"> with one <li> per item; each
//     item's "Confirm →" affordance is a routed <Link>; lucide glyphs decorative
//     (aria-hidden).

const AttentionList: React.FC<{ items: AttentionItem[] }> = ({ items }) => {
  if (items.length === 0) {
    return (
      <p className="ap-attention-empty">Nothing needs your attention right now.</p>
    );
  }

  return (
    <ul className="ap-attention-list">
      {items.map((item) => {
        const isUrgent = item.urgent === true;
        return (
          <li
            key={item.id}
            className={`ap-attention-item${isUrgent ? ' ap-attention-item--urgent' : ''}`}
          >
            <span
              className={`ap-attention-icon${isUrgent ? ' ap-attention-icon--urgent' : ''}`}
              aria-hidden="true"
              {...(isUrgent ? { 'data-reserved-colour': 'urgent' } : {})}
            >
              {isUrgent ? <AlertTriangle size={18} /> : <Circle size={16} />}
            </span>
            <span className="ap-attention-body">
              <span className="ap-attention-titlerow">
                <span className="ap-attention-title">{item.title}</span>
                {/* Urgent state as TEXT — never colour alone (FR-015). */}
                {isUrgent && <span className="ap-attention-urgent-tag">Urgent</span>}
                {/* Illustrative items wear the shared badge; REAL items do not. */}
                {item.provenance === 'illustrative' && (
                  <ProvenanceBadge source="illustrative" />
                )}
              </span>
              {item.detail && <span className="ap-attention-detail">{item.detail}</span>}
            </span>
            <Link
              to={item.confirmRoute}
              className={`ap-attention-confirm${
                isUrgent ? ' ap-attention-confirm--urgent' : ''
              }`}
            >
              Confirm
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default AttentionList;
